import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"

import {
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import {
  generateBcbaGoals,
  GeneratedGoal as Goal,
} from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "bcba_goal_generator"

const BcbaGoalGenerator = requireAuth(() => {
  const [topic, setTopic] = useState("")
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, { topic_length: topic.length })
    try {
      const data = await generateBcbaGoals(topic)
      setGoals(data.goals)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate goals. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Parent Training Goal Generator"
      description="Enter what you want parents to learn. We'll generate 5 SMART parent training goals with mastery criteria."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          What do you want them to learn?
        </Typography>
        <TextInput
          name="Topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., token economy system"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate goals"
        onClick={generate}
        disabled={loading || !topic}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {goals.length > 0 && (
        <FlexBox direction="column" gap={SPACING.space3}>
          <Typography variant="h3" textAlign="left">
            Generated goals
          </Typography>
          {goals.map((g, i) => (
            <FlexBox
              key={i}
              direction="column"
              gap={SPACING.space2}
              style={{
                border: `1px solid ${JoonUIColor.border.default}`,
                borderRadius: SPACING.space2,
                padding: SPACING.space4,
              }}
            >
              <Typography variant="bodyBold" textAlign="left">
                {g.title}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Goal:</strong> {g.goal}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Mastery criteria:</strong> {g.masteryCriteria}
              </Typography>
            </FlexBox>
          ))}
        </FlexBox>
      )}
    </ToolPage>
  )
})

export default BcbaGoalGenerator
